import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Game } from './game/Game';
import { loadLevels, Collection } from './game/levels';
import { Grid } from './components/Grid';
import { LevelSelector } from './components/LevelSelector';
import { CollectionSelector } from './components/CollectionSelector';
import { Controls } from './components/Controls';
import './App.css';

const CELL_SIZE = 40;
const PLAYBACK_DELAY = 100;

type Direction = 'up' | 'down' | 'left' | 'right';

const directionMap: Record<string, Direction | null> = {
  'u': 'up', 'U': 'up',
  'd': 'down', 'D': 'down',
  'l': 'left', 'L': 'left',
  'r': 'right', 'R': 'right',
};

const directionToDelta: Record<Direction, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

function App() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [currentCollection, setCurrentCollection] = useState<string>('');
  const [currentLevelId, setCurrentLevelId] = useState<number>(1);
  const [game, setGame] = useState<Game | null>(null);
  const [moveInput, setMoveInput] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const playbackRef = useRef<number | null>(null);

  useEffect(() => {
    loadLevels().then((loadedCollections) => {
      setCollections(loadedCollections);
      if (loadedCollections.length > 0) {
        setCurrentCollection(loadedCollections[0].name);
      }
    });
  }, []);

  const currentCollectionData = collections.find(c => c.name === currentCollection);
  const currentLevel = currentCollectionData?.levels.find(l => l.id === currentLevelId);

  useEffect(() => {
    if (currentLevel) {
      const newGame = new Game(currentLevel.matrix, currentLevel.limit);
      setGame(newGame);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevelId, currentCollection]);

  const handleMove = useCallback((dx: number, dy: number) => {
    if (game && !game.isCompleted() && !game.isFailed()) {
      game.move(dx, dy);
      setGame(game.clone());
    }
  }, [game]);

  const handleUndo = useCallback(() => {
    if (game) {
      game.unmove();
      setGame(game.clone());
    }
  }, [game]);

  const handleStopPlayback = useCallback(() => {
    if (playbackRef.current) {
      clearTimeout(playbackRef.current);
      playbackRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const handleReset = useCallback(() => {
    handleStopPlayback();
    if (currentLevel) {
      const newGame = new Game(currentLevel.matrix, currentLevel.limit);
      setGame(newGame);
      setMoveInput('');
    }
  }, [currentLevel, handleStopPlayback]);

  const handleSelectLevel = useCallback((levelId: number) => {
    handleReset();
    setCurrentLevelId(levelId);
  }, [handleReset]);

  const handleSelectCollection = useCallback((collection: string) => {
    handleReset();
    setCurrentCollection(collection);
    setCurrentLevelId(1);
  }, [handleReset]);

  const handleNextLevel = useCallback(() => {
    if (currentCollectionData) {
      const currentIndex = currentCollectionData.levels.findIndex(l => l.id === currentLevelId);
      if (currentIndex < currentCollectionData.levels.length - 1) {
        setCurrentLevelId(currentCollectionData.levels[currentIndex + 1].id);
      }
    }
  }, [currentLevelId, currentCollectionData]);

  const handlePlayback = useCallback(() => {
    const moves = moveInput.split('').filter(m => directionMap[m] !== null);
    if (moves.length === 0) return;

    setIsPlaying(true);
    let index = 0;

    const playStep = () => {
      if (index >= moves.length) {
        setIsPlaying(false);
        return;
      }

      const dir = directionMap[moves[index]];
      if (dir && game && !game.isCompleted() && !game.isFailed()) {
        const delta = directionToDelta[dir];
        game.move(delta.dx, delta.dy);
        setGame(game.clone());
      }

      index++;
      playbackRef.current = window.setTimeout(playStep, PLAYBACK_DELAY);
    };

    playStep();
  }, [moveInput, game]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePlayback();
    }
  }, [handlePlayback]);

  useEffect(() => {
    return () => {
      if (playbackRef.current) {
        clearTimeout(playbackRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInputFocused = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      const isMovementKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'W', 's', 'S', 'a', 'A', 'd', 'D'].includes(e.key);
      const isPlaybackKey = ['u', 'U', 'd', 'D', 'l', 'L', 'r', 'R'].includes(e.key);

      if (isInputFocused) {
        if (isMovementKey && !isPlaybackKey) {
          e.preventDefault();
          return;
        }
        return;
      }

      if (isPlaybackKey || isMovementKey) {
        e.preventDefault();
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          handleMove(0, -1);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          handleMove(0, 1);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          handleMove(-1, 0);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          handleMove(1, 0);
          break;
        case 'u':
        case 'U':
          handleMove(0, -1);
          break;
        case 'l':
        case 'L':
          handleMove(-1, 0);
          break;
        case 'r':
        case 'R':
          handleMove(1, 0);
          break;
        case 'z':
        case 'Z':
          handleUndo();
          break;
        case 'n':
        case 'N':
          handleNextLevel();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMove, handleUndo, handleReset, handleNextLevel]);

  const isCompleted = game?.isCompleted() ?? false;

  return (
    <div className="App">
      <h1 className="title">Sokoban</h1>
      
      <CollectionSelector
        collections={collections}
        currentCollection={currentCollection}
        onSelectCollection={handleSelectCollection}
      />
      
      {currentCollectionData && (
        <LevelSelector
          levels={currentCollectionData.levels}
          currentLevelId={currentLevelId}
          onSelectLevel={handleSelectLevel}
        />
      )}
      
      <div className="game-container">
        {game && <Grid matrix={game.getMatrix()} cellSize={CELL_SIZE} />}
      </div>
      
      <Controls
        onUndo={handleUndo}
        canUndo={game?.canUndo() ?? false}
        onReset={handleReset}
        isCompleted={isCompleted}
        stepCount={game?.getStepCount() ?? 0}
        limit={currentLevel?.limit}
        isFailed={game?.isFailed() ?? false}
        moveInput={moveInput}
        onMoveInputChange={setMoveInput}
        onPlayback={handlePlayback}
        onStopPlayback={handleStopPlayback}
        isPlaying={isPlaying}
        onInputKeyDown={handleInputKeyDown}
      />
    </div>
  );
}

export default App;